import { BACKEND_URL } from '../config/apiBase';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Truck, Languages, Mail, Shield, ArrowLeft, Loader2, CheckCircle, GraduationCap, Play, Trophy, CreditCard, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = BACKEND_URL;

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  // Steps: 'email' | 'otp' | 'success'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  
  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0) {
      timerRef.current = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [timeLeft]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid fetch polyfill issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/auth/request-otp`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch (e) {
            reject(new Error('Error parsing response'));
          }
        };
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        xhr.send(JSON.stringify({ email: email.trim().toLowerCase() }));
      });

      if (result.status === 429) {
        setError(result.data.detail || 'Demasiadas solicitudes. Espere unos minutos.');
        toast.error(result.data.detail || 'Demasiadas solicitudes');
      } else if (result.data.success) {
        setStep('otp');
        setTimeLeft(300); // 5 minutes
        toast.success('Código enviado a tu correo');
        // Focus first OTP input
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError('Error de conexión. Intente nuevamente.');
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only last digit
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (newOtp.every(d => d) && newOtp.join('').length === 6) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
      handleVerifyOTP(pastedData);
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    setError('');
    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid fetch polyfill issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/auth/verify-otp`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
          } catch (e) {
            reject(new Error('Error parsing response'));
          }
        };
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        xhr.send(JSON.stringify({ 
          email: email.trim().toLowerCase(),
          otp_code: otpCode 
        }));
      });

      if (!result.ok) {
        setError(result.data.detail || 'Código incorrecto');
        toast.error(result.data.detail || 'Código incorrecto');
        // Clear OTP on error
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      } else if (result.data.success && result.data.access_token) {
        setStep('success');
        
        // Login with token (this also syncs the user's language preference)
        await loginWithToken(result.data.access_token, result.data.user);
        
        // Get user's preferred language for the toast message
        const userLang = result.data.user?.preferred_language || result.data.user?.training_language || i18n.language;
        toast.success(userLang === 'es' ? '¡Verificación exitosa!' : 'Verification successful!');
        
        // Navigate based on role - trainees go to training, others to dashboard
        const userRole = result.data.user?.role;
        const destination = userRole === 'trainee' ? '/training' : '/dashboard';
        setTimeout(() => navigate(destination), 1000);
      }
    } catch (err) {
      setError(i18n.language === 'es' ? 'Error de conexión. Intente nuevamente.' : 'Connection error. Please try again.');
      toast.error(i18n.language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (timeLeft > 240) return; // Can't resend in first 60 seconds
    
    setOtp(['', '', '', '', '', '']);
    setError('');
    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid fetch polyfill issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/auth/request-otp`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch (e) {
            reject(new Error('Error parsing response'));
          }
        };
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        xhr.send(JSON.stringify({ email: email.trim().toLowerCase() }));
      });

      if (result.status === 429) {
        setError(result.data.detail);
        toast.error(result.data.detail);
      } else {
        setTimeLeft(300);
        toast.success('Nuevo código enviado');
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Error al reenviar código');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setOtp(['', '', '', '', '', '']);
    setError('');
    setTimeLeft(0);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-primary">
        <img 
          src="https://images.unsplash.com/photo-1562811950-41d4a4944a4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjB0cnVjayUyMGZsZWV0JTIwYWJzdHJhY3R8ZW58MHx8fHwxNzY3ODQyMzA5fDA&ixlib=rb-4.1.0&q=85"
          alt="Truck fleet"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
        <div className="absolute inset-0 flex flex-col justify-center items-center text-primary-foreground p-12">
          <div className="flex items-center gap-3 mb-6">
            <Truck className="w-12 h-12" />
            <span className="text-4xl font-bold tracking-tight font-['Manrope']">INSPIRED FUNDING</span>
          </div>
          <p className="text-xl text-center max-w-md opacity-90">
            {t('app_subtitle')}
          </p>
          
          {/* Security badges - Updated to show new features */}
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm">{t('feature_credit')}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Truck className="w-4 h-4" />
              <span className="text-sm">{t('feature_appraisals')}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Building2 className="w-4 h-4" />
              <span className="text-sm">{t('feature_bank')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleLanguage}
            className="flex items-center gap-2"
            data-testid="language-toggle"
          >
            <Languages className="w-4 h-4" />
            {i18n.language === 'es' ? 'EN' : 'ES'}
          </Button>
        </div>

        <Card className="w-full max-w-md shadow-lg border-border">
          {/* Email Step */}
          {step === 'email' && (
            <>
              <CardHeader className="space-y-1 text-center">
                <div className="flex justify-center mb-4 lg:hidden">
                  <div className="flex items-center gap-2 text-primary">
                    <Truck className="w-8 h-8" />
                    <span className="text-xl font-bold font-['Manrope']">INSPIRED FUNDING</span>
                  </div>
                </div>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold font-['Manrope']">
                  {i18n.language === 'es' ? 'Acceso Seguro' : 'Secure Access'}
                </CardTitle>
                <CardDescription>
                  {i18n.language === 'es' 
                    ? 'Ingresa tu correo corporativo para recibir un código de verificación'
                    : 'Enter your corporate email to receive a verification code'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRequestOTP} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">{i18n.language === 'es' ? 'Correo Electrónico' : 'Email Address'}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={i18n.language === 'es' ? 'tu.correo@empresa.com' : 'your.email@company.com'}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="off"
                      autoFocus
                      data-testid="email-input"
                      className="h-12 text-base"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-semibold text-base"
                    disabled={loading || !email}
                    data-testid="request-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {i18n.language === 'es' ? 'Enviando...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        {i18n.language === 'es' ? 'Enviar Código de Verificación' : 'Send Verification Code'}
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground text-center">
                    🔒 {i18n.language === 'es' 
                      ? 'Tu seguridad es nuestra prioridad. Usamos autenticación de dos factores para proteger tu cuenta y la información de los clientes.'
                      : 'Your security is our priority. We use two-factor authentication to protect your account and client information.'}
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <>
              <CardHeader className="space-y-1 text-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBack}
                  className="absolute top-4 left-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {i18n.language === 'es' ? 'Atrás' : 'Back'}
                </Button>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold font-['Manrope']">
                  {i18n.language === 'es' ? 'Verificación OTP' : 'OTP Verification'}
                </CardTitle>
                <CardDescription>
                  {i18n.language === 'es' 
                    ? <>Ingresa el código de 6 dígitos enviado a<br /></>
                    : <>Enter the 6-digit code sent to<br /></>}
                  <span className="font-medium text-foreground">{email}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}
                  
                  {/* OTP Input */}
                  <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        ref={el => otpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-primary"
                        data-testid={`otp-input-${index}`}
                        disabled={loading}
                      />
                    ))}
                  </div>

                  {/* Timer */}
                  <div className="text-center">
                    {timeLeft > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {i18n.language === 'es' ? 'Código expira en: ' : 'Code expires in: '}
                        <span className="font-mono font-bold text-primary">{formatTime(timeLeft)}</span>
                      </p>
                    ) : (
                      <p className="text-sm text-destructive font-medium">
                        {i18n.language === 'es' ? 'El código ha expirado' : 'Code has expired'}
                      </p>
                    )}
                  </div>

                  {/* Verify Button */}
                  <Button 
                    onClick={() => handleVerifyOTP(otp.join(''))}
                    className="w-full h-12 font-semibold text-base"
                    disabled={loading || otp.some(d => !d)}
                    data-testid="verify-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {i18n.language === 'es' ? 'Verificando...' : 'Verifying...'}
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-2" />
                        {i18n.language === 'es' ? 'Verificar e Ingresar' : 'Verify and Sign In'}
                      </>
                    )}
                  </Button>

                  {/* Resend */}
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                      {i18n.language === 'es' ? "¿No recibiste el código?" : "Didn't receive the code?"}
                    </p>
                    <Button 
                      variant="link" 
                      onClick={handleResendOTP}
                      disabled={loading || timeLeft > 240}
                      className="text-primary"
                    >
                      {timeLeft > 240 
                        ? (i18n.language === 'es' ? `Reenviar en ${formatTime(timeLeft - 240)}` : `Resend in ${formatTime(timeLeft - 240)}`)
                        : (i18n.language === 'es' ? 'Reenviar Código' : 'Resend Code')
                      }
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">
                {i18n.language === 'es' ? '¡Verificación Exitosa!' : 'Verification Successful!'}
              </h2>
              <p className="text-muted-foreground mb-4">
                {i18n.language === 'es' ? 'Redirigiendo al dashboard...' : 'Redirecting to dashboard...'}
              </p>
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
            </CardContent>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          <p>© 2026 Inspired Funding LLC. {i18n.language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}</p>
          <p className="mt-1">{i18n.language === 'es' ? 'Sistema protegido con autenticación OTP y Zero Trust' : 'System protected with OTP authentication and Zero Trust'}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
